import React, { useEffect } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";

import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [44.8659, -0.5972];

const infrastructureIcon = new L.DivIcon({
  className: "custom-infrastructure-marker",
  html: `
    <div style="
      width: 24px;
      height: 24px;
      border-radius: 999px;
      background: #2563eb;
      border: 4px solid white;
      box-shadow: 0 10px 25px rgba(37,99,235,0.4);
    "></div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const selectedInfrastructureIcon = new L.DivIcon({
  className: "custom-selected-infrastructure-marker",
  html: `
    <div style="
      width: 32px;
      height: 32px;
      border-radius: 999px;
      background: #16a34a;
      border: 5px solid white;
      box-shadow: 0 12px 30px rgba(22,163,74,0.5);
    "></div>
  `,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const hasCoordinates = (item) =>
  Number.isFinite(Number(item?.latitude)) &&
  Number.isFinite(Number(item?.longitude));

const MapController = ({ infrastructures, selectedInfrastructure }) => {
  const map = useMap();

  useEffect(() => {
    const timeout = setTimeout(() => {
      map.invalidateSize();
    }, 150);

    return () => clearTimeout(timeout);
  }, [map]);

  useEffect(() => {
    if (hasCoordinates(selectedInfrastructure)) {
      map.flyTo(
        [
          Number(selectedInfrastructure.latitude),
          Number(selectedInfrastructure.longitude),
        ],
        16,
        { duration: 1.1 }
      );

      return;
    }

    const validInfrastructures = infrastructures.filter(hasCoordinates);

    if (validInfrastructures.length === 1) {
      map.flyTo(
        [
          Number(validInfrastructures[0].latitude),
          Number(validInfrastructures[0].longitude),
        ],
        15,
        { duration: 1 }
      );

      return;
    }

    if (validInfrastructures.length > 1) {
      const bounds = L.latLngBounds(
        validInfrastructures.map((infrastructure) => [
          Number(infrastructure.latitude),
          Number(infrastructure.longitude),
        ])
      );

      map.fitBounds(bounds, {
        padding: [48, 48],
        maxZoom: 16,
      });
    }
  }, [map, infrastructures, selectedInfrastructure]);

  return null;
};

const MapComponent = ({
  infrastructures = [],
  selectedInfrastructure = null,
  onSelectInfrastructure,
}) => {
  const validInfrastructures = infrastructures.filter(hasCoordinates);

  const initialCenter =
    validInfrastructures.length > 0
      ? [
          Number(validInfrastructures[0].latitude),
          Number(validInfrastructures[0].longitude),
        ]
      : DEFAULT_CENTER;

  return (
    <MapContainer
      center={initialCenter}
      zoom={15}
      style={{ height: "100%", width: "100%", zIndex: 0 }}
      scrollWheelZoom
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />

      <MapController
        infrastructures={validInfrastructures}
        selectedInfrastructure={selectedInfrastructure}
      />

      {validInfrastructures.map((infrastructure) => {
        const isSelected =
          String(selectedInfrastructure?.id) === String(infrastructure.id);

        return (
          <Marker
            key={infrastructure.id}
            position={[
              Number(infrastructure.latitude),
              Number(infrastructure.longitude),
            ]}
            icon={
              isSelected ? selectedInfrastructureIcon : infrastructureIcon
            }
            eventHandlers={{
              click: () => {
                if (onSelectInfrastructure) {
                  onSelectInfrastructure(infrastructure);
                }
              },
            }}
          >
            <Popup>
              <div className="min-w-[220px]">
                <h3 className="text-base font-bold text-slate-900">
                  {infrastructure.nom_infrastructure}
                </h3>

                {infrastructure.adresse_infrastructure && (
                  <p className="mt-2 text-xs leading-5 text-slate-600">
                    {infrastructure.adresse_infrastructure}
                  </p>
                )}

                {infrastructure.description_infrastructure && (
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    {infrastructure.description_infrastructure}
                  </p>
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (onSelectInfrastructure) {
                      onSelectInfrastructure(infrastructure);
                    }
                  }}
                  className="mt-3 rounded-xl bg-blue-600 px-3 py-2 text-xs font-bold text-white"
                >
                  Voir le détail
                </button>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

export default MapComponent;